import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Download, BellRing } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { endpoints } from "@/lib/api";
import { Card, Btn, handleErr, fmtDateTime } from "@/pages/admin/AdminUI";

const inp = "h-9 rounded-lg bg-[#0A0C10] border border-[#273042] px-3 text-white text-sm outline-none focus:border-[#E11D2A]/60";

export const SmsUsage = ({ cfg, onSave }) => {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [prices, setPrices] = useState(cfg.smsPrices || { galaxy: 15, swiftqom: 15 });
  useEffect(() => { endpoints.adminSmsUsage(days).then((r) => setD(r.data)).catch(handleErr); }, [days, cfg.smsPrices]);
  const csv = () => {
    const head = "date,IZERE_sent,IZERE_failed,IZERE_cost_RWF,BEPAY_sent,BEPAY_failed,BEPAY_cost_RWF";
    const lines = d.rows.map((r) => [r.date, r.galaxy.sent, r.galaxy.failed, r.galaxy.cost, r.swiftqom.sent, r.swiftqom.failed, r.swiftqom.cost].join(","));
    const url = URL.createObjectURL(new Blob([[head, ...lines].join("\n")], { type: "text/csv" }));
    Object.assign(document.createElement("a"), { href: url, download: `sms-usage-${days}d.csv` }).click();
  };
  const T = d?.totals;
  return (
    <Card className="p-5 mt-6" data-testid="sms-usage-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-white font-bold flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#E11D2A]" /> SMS Usage & cost</div>
        <div className="flex flex-wrap items-center gap-2">
          {[7, 30, 90].map((n) => <button key={n} onClick={() => setDays(n)} data-testid={`sms-usage-range-${n}`}
            className={`h-8 px-3 rounded-full text-xs font-semibold ${days === n ? "bg-[#E11D2A] text-white" : "bg-white/5 text-slate-300"}`}>{n} days</button>)}
          <Btn variant="ghost" onClick={csv} disabled={!d?.rows?.length} data-testid="sms-usage-csv"><Download className="w-4 h-4" /> CSV</Btn>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4 text-xs text-slate-300">
        <label>IZERE price (RWF/SMS)<input type="number" min="0" value={prices.galaxy} onChange={(e) => setPrices({ ...prices, galaxy: e.target.value })} className={`${inp} block w-28 mt-1`} data-testid="sms-price-galaxy" /></label>
        <label>BEPAY price (RWF/SMS)<input type="number" min="0" value={prices.swiftqom} onChange={(e) => setPrices({ ...prices, swiftqom: e.target.value })} className={`${inp} block w-28 mt-1`} data-testid="sms-price-swiftqom" /></label>
        <Btn onClick={() => onSave({ ...cfg, smsPrices: { galaxy: Number(prices.galaxy) || 0, swiftqom: Number(prices.swiftqom) || 0 } })} data-testid="sms-price-save">Save prices</Btn>
      </div>
      {T && (
        <div className="grid sm:grid-cols-2 gap-3 mb-4" data-testid="sms-usage-totals">
          {[["galaxy", "IZERE (OTP)"], ["swiftqom", "BEPAY (notifications)"]].map(([p, l]) => (
            <div key={p} className="rounded-xl bg-[#0A0C10] border border-[#273042] p-4">
              <div className="text-xs text-slate-400">{l} · last {days} days</div>
              <div className="text-2xl font-extrabold text-white mt-1" data-testid={`sms-total-${p}`}>{T[p].sent.toLocaleString()} <span className="text-sm text-slate-400 font-semibold">sent</span></div>
              <div className="text-xs text-slate-400">{T[p].failed} failed · est. <b className="text-white">{T[p].cost.toLocaleString()} RWF</b></div>
            </div>
          ))}
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="sms-usage-table">
          <thead><tr className="text-left text-xs text-slate-400 border-b border-[#273042]">
            <th className="py-2 pr-3">Date</th><th className="pr-3">IZERE sent</th><th className="pr-3">IZERE failed</th><th className="pr-3">IZERE cost</th><th className="pr-3">BEPAY sent</th><th className="pr-3">BEPAY failed</th><th>BEPAY cost</th>
          </tr></thead>
          <tbody>
            {(d?.rows || []).map((r) => (
              <tr key={r.date} className="border-b border-[#273042]/60 text-slate-200">
                <td className="py-2 pr-3">{r.date}</td><td className="pr-3">{r.galaxy.sent}</td><td className="pr-3 text-red-300">{r.galaxy.failed}</td><td className="pr-3">{r.galaxy.cost} RWF</td>
                <td className="pr-3">{r.swiftqom.sent}</td><td className="pr-3 text-red-300">{r.swiftqom.failed}</td><td>{r.swiftqom.cost} RWF</td>
              </tr>
            ))}
            {d && !d.rows.length && <tr><td colSpan={7} className="py-6 text-center text-slate-500">No SMS sent in this period yet (counting started with this update).</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export const AlertSettings = ({ cfg, onSave }) => {
  const a = cfg.alerts || {};
  const [phones, setPhones] = useState((a.phones || []).join(", "));
  const [emails, setEmails] = useState((a.emails || []).join(", "));
  const [threshold, setThreshold] = useState(a.threshold || 3);
  const [recent, setRecent] = useState(null);
  useEffect(() => { endpoints.adminApiAlerts().then((r) => setRecent(r.data)).catch(() => {}); }, [cfg]);
  const split = (v) => v.split(/[,\s]+/).map((x) => x.trim()).filter(Boolean);
  const save = (enabled = a.enabled !== false) => {
    if (!split(phones).length && !split(emails).length) return toast.error("Add at least one phone or email for alerts");
    onSave({ ...cfg, alerts: { ...a, enabled, threshold: Number(threshold) || 3, phones: split(phones), emails: split(emails) } });
  };
  return (
    <Card className="p-5 mt-6" data-testid="alert-settings-panel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-white font-bold flex items-center gap-2"><BellRing className="w-5 h-5 text-[#E11D2A]" /> Provider-down alerts</div>
        <Switch checked={a.enabled !== false} onCheckedChange={(v) => save(v)} data-testid="alerts-enabled-toggle" aria-label="Provider alerts" />
      </div>
      <p className="text-xs text-slate-400 mb-4">When an SMS, WhatsApp or payment API fails {a.threshold || 3} times in a row, admins get an SMS alert (through a working SMS API) and an email — max one per API every {a.cooldownMin || 30} min — plus a "working again" message when it recovers.</p>
      <div className="grid sm:grid-cols-3 gap-3 text-xs text-slate-300">
        <label className="sm:col-span-1">Alert phones (comma separated)<input value={phones} onChange={(e) => setPhones(e.target.value)} className={`${inp} w-full mt-1`} data-testid="alert-phones-input" /></label>
        <label className="sm:col-span-1">Alert emails<input value={emails} onChange={(e) => setEmails(e.target.value)} className={`${inp} w-full mt-1`} data-testid="alert-emails-input" /></label>
        <label>Failures in a row before alert<input type="number" min="1" max="20" value={threshold} onChange={(e) => setThreshold(e.target.value)} className={`${inp} w-full mt-1`} data-testid="alert-threshold-input" /></label>
      </div>
      <Btn className="mt-3" onClick={() => save()} data-testid="alert-settings-save">Save alert settings</Btn>
      {recent?.alerts?.length > 0 && (
        <div className="mt-4 space-y-1.5" data-testid="recent-alerts">
          <div className="text-xs text-slate-400 font-semibold">Recent alerts</div>
          {recent.alerts.slice(0, 8).map((x) => (
            <div key={x.id} className={`text-xs ${x.kind === "down" ? "text-red-300" : "text-emerald-300"}`}>{fmtDateTime(x.createdAt)} · {x.label} {x.kind === "down" ? "FAILING" : "recovered"} {x.detail && `— ${x.detail.slice(0, 90)}`}</div>
          ))}
        </div>
      )}
    </Card>
  );
};
